import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AdvancedAnalyticsRequester, type AdvancedAnalyticsStatus, type OrgGroup } from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const advancedAnalyticsRequester = new AdvancedAnalyticsRequester({
  host: env.apiHost,
});

const QUERY_KEY = 'ADVANCED_ANALYTICS';
const useAdvancedAnalytics = () => {
  const queryClient = useQueryClient();

  const refreshDashboardLinkCache = (link: string | undefined) => {
    const dashboardQueryKey = [QUERY_KEY, 'USER_DASHBOARD'];
    queryClient.setQueryData<string>(dashboardQueryKey, link);
  };

  const getDwStatus = (enabled = true) =>
    useQuery<AdvancedAnalyticsStatus>({
      queryKey: [QUERY_KEY, 'DW_STATUS'],
      queryFn: () => advancedAnalyticsRequester.getDwStatus(),
      enabled,
    });

  const requestDwCreation = useMutation({
    mutationFn: advancedAnalyticsRequester.requestDwCreation,
  });

  const getDwOrgGroup = useQuery<OrgGroup>({
    queryKey: [QUERY_KEY, 'ORG_GROUP'],
    queryFn: () => advancedAnalyticsRequester.getDwOrgGroup(),
    refetchOnMount: false,
  });

  const getUserDashboard = useQuery<string | undefined>({
    queryKey: [QUERY_KEY, 'USER_DASHBOARD'],
    queryFn: () => advancedAnalyticsRequester.getUserDashboard(),
    refetchOnMount: false,
  });

  const saveUserDashboard = useMutation({
    mutationFn: advancedAnalyticsRequester.saveUserDashboard,
    onMutate: async (dashboardLink) => {
      const dashboardQueryKey = [QUERY_KEY, 'USER_DASHBOARD'];
      await queryClient.cancelQueries({ queryKey: dashboardQueryKey });
      const previousLink: string | undefined = queryClient.getQueryData(dashboardQueryKey);

      refreshDashboardLinkCache(dashboardLink);

      return { previousLink };
    },
    onError: (err, dashboardLink, context) => {
      refreshDashboardLinkCache(context?.previousLink);
    },
  });

  return {
    getDwStatus,
    requestDwCreation,
    getDwOrgGroup,
    getUserDashboard,
    saveUserDashboard,
  };
};

export default useAdvancedAnalytics;
